import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { ChevronLeft } from "lucide-react";
import { PageHeader } from "@/components/app-shell";
import { CalcShell, Field, NumberInput, Select, ResultCard, fmt } from "@/components/calculators/CalcShell";
import { AddToQuoteButton } from "@/components/calculators/AddToQuoteButton";
import { linesFromElectricianNote } from "@/components/calculators/lineMappers";
import { power, type Phase } from "@/domain/calculators";

export const Route = createFileRoute("/_authenticated/calculations/power")({
  component: PowerPage,
});

function PowerPage() {
  const [phase, setPhase] = useState<"1" | "3">("1");
  const [cosPhi, setCos] = useState<number | "">(0.9);
  const [P, setP] = useState<number | "">("");
  const [U, setU] = useState<number | "">(phase === "3" ? 400 : 230);
  const [I, setI] = useState<number | "">("");

  const result = useMemo(() => {
    const provided = [P, U, I].filter((v) => v !== "").length;
    if (provided !== 2 || cosPhi === "") return null;
    try {
      return power({
        phase: Number(phase) as Phase,
        cosPhi,
        P: P === "" ? undefined : P,
        U: U === "" ? undefined : U,
        I: I === "" ? undefined : I,
      });
    } catch (e) {
      return { error: (e as Error).message };
    }
  }, [phase, cosPhi, P, U, I]);

  return (
    <>
      <Link to="/calculations" className="mb-4 inline-flex items-center text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-4 w-4" /> Beregninger
      </Link>
      <PageHeader title="Effekt" subtitle="1-fase: P = U·I·cosφ    3-fase: P = √3·U·I·cosφ" />
      <CalcShell
        title="Inndata"
        description="La ett av P, U, I stå tomt."
        result={
          result && "error" in result ? (
            <div className="card p-5 text-sm text-rose-600">{result.error}</div>
          ) : result ? (
            <>
            <ResultCard
              title={`Beregnet ${result.solvedFor}`}
              rows={[
                { label: "Effekt P",   value: `${fmt(result.P)} W (${fmt(result.P / 1000, 3)} kW)`, strong: result.solvedFor === "P" },
                { label: "Spenning U", value: `${fmt(result.U)} V`, strong: result.solvedFor === "U" },
                { label: "Strøm I",    value: `${fmt(result.I)} A`, strong: result.solvedFor === "I" },
                { label: "cosφ",       value: fmt(result.cosPhi, 2) },
                { label: "Fase",       value: `${result.phase}-fase` },
              ]}
            />
            <div className="mt-3 flex justify-end">
              <AddToQuoteButton sourceLabel={`Effektberegning`} lines={linesFromElectricianNote(`Effektuttak`)} />
            </div>
          </>
          ) : <div className="card p-5 text-sm text-muted-foreground">Fyll inn to av P, U, I.</div>
        }
      >
        <Field label="Fase">
          <Select value={phase} onChange={setPhase} options={[
            { value: "1", label: "1-fase" }, { value: "3", label: "3-fase" },
          ]} />
        </Field>
        <Field label="cosφ"><NumberInput value={cosPhi} onChange={setCos} /></Field>
        <Field label="Effekt P" unit="W"><NumberInput value={P} onChange={setP} /></Field>
        <Field label="Spenning U" unit="V"><NumberInput value={U} onChange={setU} /></Field>
        <Field label="Strøm I" unit="A"><NumberInput value={I} onChange={setI} /></Field>
      </CalcShell>
    </>
  );
}
